import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import { isPrerenderedPath } from './components/platformPageMeta';
import { ErrorBoundary } from './components/ErrorBoundary';
import { installErrorReporter } from './components/errorReporter';
import { installRouter } from './lib/router';
import { shouldHydrateRoot } from './lib/rootMount';

// Capture uncaught errors / rejections and forward them to the backend for the
// monitoring/ source-map enricher + codex auto-fixer.
installErrorReporter();

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error("Could not find root element to mount to");
}

const app = (
  <React.StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </React.StrictMode>
);

// Firebase email-action links (password reset) land on the configured action URL
// — typically the homepage — as ?mode=resetPassword&oobCode=...; without this they
// fall through to the homepage instead of the reset UI. Normalize them onto
// /reset-password (preserving the query) before we decide how to mount, so the
// client-only route does a clean render and ResetPasswordPanel sees the oobCode.
{
  const q = new URLSearchParams(window.location.search);
  if (window.location.pathname !== '/reset-password' && (q.get('mode') === 'resetPassword' || q.get('oobCode'))) {
    window.history.replaceState(null, '', '/reset-password' + window.location.search);
  }
}

// Static marketing routes are prerendered to their own dist/<path>/index.html, so
// the served markup matches the current route and we can hydrate. Interactive
// routes (/agents, /sites, …) are served the homepage shell, so do a clean client
// render there to avoid hydration mismatches (React #418).
const pathname = window.location.pathname;

let root: ReactDOM.Root;
if (shouldHydrateRoot(rootElement, isPrerenderedPath(pathname))) {
  root = ReactDOM.hydrateRoot(rootElement, app);
} else {
  rootElement.innerHTML = '';
  root = ReactDOM.createRoot(rootElement);
  root.render(app);
}

// Global pushState routing: installRouter intercepts same-origin link clicks and
// patches history to emit popstate. AppRoutes subscribes to the location via
// useLocationPath, so it re-renders itself on every navigation — we deliberately
// do NOT rely on root.render(app) here, because re-rendering the same <App/>
// element hits React's same-element bailout and never updates the route.
installRouter();
